import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AxiosError } from "axios";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { MediaSheet, MediaSheetSkeleton, RatioSection } from "@/components/content-programs/media-tile";
import {
  AffiliateSubmissionPortraitTile,
  type AffiliateSubmissionRow,
} from "@/components/affiliate/content-programs/AffiliateSubmissionPortraitTile";
import { AffiliateSubmissionDialog } from "@/components/affiliate/content-programs/AffiliateSubmissionDialog";
import { useRatioGroups } from "@/hooks/useRatioGroups";
import { listAffiliateMySubmissions, retractAffiliateSubmission } from "@/services/contentProgram.service";

function errMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const d = e.response?.data as { message?: string | string[] } | undefined;
    if (d?.message) return Array.isArray(d.message) ? d.message.join(", ") : d.message;
    return e.message || "Request failed";
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

type StatusTab = "all" | "PENDING" | "CHANGE_REQUESTED" | "APPROVED" | "REJECTED";

/** Needs attention first, then the rest, newest on top within each. */
function sortRows(rows: AffiliateSubmissionRow[]) {
  const rank = (s: string) => (s === "CHANGE_REQUESTED" ? 0 : s === "PENDING" ? 1 : 2);
  return [...rows].sort((a, b) => {
    const r = rank(a.status) - rank(b.status);
    if (r !== 0) return r;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

export default function AffiliateMySubmissions() {
  const navigate = useNavigate();
  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["affiliate-my-content-submissions"],
    queryFn: listAffiliateMySubmissions,
  });

  const retractMut = useMutation({
    mutationFn: retractAffiliateSubmission,
    onSuccess: () => {
      toast.success("Submission withdrawn");
      setSelectedId(null);
      void q.refetch();
    },
    onError: (e) => toast.error(errMessage(e)),
  });

  const rows = useMemo(() => (q.data ?? []) as AffiliateSubmissionRow[], [q.data]);

  const counts = useMemo(() => {
    const c = { all: rows.length, PENDING: 0, CHANGE_REQUESTED: 0, APPROVED: 0, REJECTED: 0 };
    for (const r of rows) {
      if (r.status === "PENDING") c.PENDING += 1;
      else if (r.status === "CHANGE_REQUESTED") c.CHANGE_REQUESTED += 1;
      else if (r.status === "APPROVED" || r.status === "IN_USE") c.APPROVED += 1;
      else if (r.status === "REJECTED") c.REJECTED += 1;
    }
    return c;
  }, [rows]);

  const visible = useMemo(
    () =>
      sortRows(
        statusTab === "all"
          ? rows
          : rows.filter((r) =>
              statusTab === "APPROVED" ? r.status === "APPROVED" || r.status === "IN_USE" : r.status === statusTab
            )
      ),
    [rows, statusTab]
  );

  const { orderedGroups, loading: ratiosLoading, getRatioLabel, getMetaRatioLabel } = useRatioGroups(visible);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  const statusTabs = [
    { value: "all" as const, label: "All", count: counts.all },
    { value: "PENDING" as const, label: "Awaiting review", count: counts.PENDING },
    { value: "CHANGE_REQUESTED" as const, label: "Changes requested", count: counts.CHANGE_REQUESTED },
    { value: "APPROVED" as const, label: "Approved", count: counts.APPROVED },
    { value: "REJECTED" as const, label: "Rejected", count: counts.REJECTED },
  ];

  return (
    <>
      <DashboardCard
        title="Submissions"
        fixed={<UnderlineTabs className="mx-5" tabs={statusTabs} value={statusTab} onValueChange={setStatusTab} accent="brand" />}
        bodyClassName="px-5 pb-5 pt-4"
      >
        {q.isLoading || (ratiosLoading && visible.length > 0) ? (
          <MediaSheetSkeleton count={Math.min(Math.max(visible.length, 3), 8)} />
        ) : visible.length === 0 ? (
          <EmptyState
            bordered={false}
            title={statusTab === "all" ? "No submissions yet" : "Nothing here"}
            body={statusTab === "all" ? "Pick a program and submit your first creative" : undefined}
            action={
              statusTab === "all" ? (
                <Button variant="outline" onClick={() => navigate("/affiliate/content-programs")}>
                  See programs
                </Button>
              ) : undefined
            }
            className="py-8"
          />
        ) : (
          <div className="space-y-5">
            {orderedGroups.map((group) => (
              <RatioSection key={group.key} ratio={group.key} count={group.items.length}>
                <MediaSheet ratio={group.key}>
                  {group.items.map((row) => (
                    <AffiliateSubmissionPortraitTile
                      key={row.id}
                      row={row}
                      ratio={getRatioLabel(row.id)}
                      onOpen={(r) => setSelectedId(r.id)}
                    />
                  ))}
                </MediaSheet>
              </RatioSection>
            ))}
          </div>
        )}
      </DashboardCard>

      <AffiliateSubmissionDialog
        submission={selected}
        ratioLabel={selected ? getMetaRatioLabel(selected.id) : null}
        onOpenChange={(open) => !open && setSelectedId(null)}
        onWithdraw={(id) => retractMut.mutate(id)}
        withdrawing={retractMut.isPending}
      />
    </>
  );
}
